/**
 * ApiClient - HTTP client with authentication integration
 * Integrates with AuthService for automatic token management and refresh
 */

import {
  LoginRequest,
  LoginResponse,
  User,
  ApiError,
  AuthConfig
} from '../types/auth';
import { DeviceCreateData, DeviceResponse } from '../types/api';
import { authService } from './authService';
import { logger } from '../utils/logger';
import i18n from '../i18n';

/** `1.0.0+c90dba3` — package version + git commit, baked in at build time. */
// Guarded: Jest (and anything not built by Vite) has no such globals.
export const APP_BUILD =
  typeof __APP_VERSION__ !== 'undefined' && typeof __APP_COMMIT__ !== 'undefined'
    ? `${__APP_VERSION__}+${__APP_COMMIT__}`
    : 'dev';

// Default configuration
const DEFAULT_CONFIG: AuthConfig = {
  apiBaseUrl: import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1',
  tokenRefreshThreshold: 300,
  maxRetries: 3,
  retryDelay: 1000,
};

/**
 * ApiClient class for handling API requests with authentication
 */
class ApiClient {
  private config: AuthConfig;
  private baseURL: string;

  constructor(config: Partial<AuthConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.baseURL = this.config.apiBaseUrl || '/api';
  }

  /**
   * Make an HTTP request with authentication and automatic retry
   * @param endpoint API endpoint
   * @param options Request options
   * @returns Response data
   */
  private async request<T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.config.apiBaseUrl}${endpoint}`;
    logger.info('DEBUG: API Request Details', {
      baseUrl: this.config.apiBaseUrl,
      endpoint,
      fullUrl: url,
      method: options.method || 'GET'
    });
    
    // Cookie mode: proactively refresh at request time if the access token is
    // near expiry, so a burst of parallel requests all await one refresh and go
    // out with a fresh cookie instead of each 401ing (immune to background-tab
    // timer throttling). No-op in dual mode / when the token is still fresh.
    await authService.ensureFreshToken();

    // Add authentication headers
    const authHeaders = authService.getAuthHeaders();

    // Send current UI language so the backend can return localised names
    const lang = i18n.language?.split('-')[0] || 'en';

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': lang,
      // Which build sent this. The app is a PWA that updates only on the
      // user's tap, so "is X on the new version?" has no other answer.
      'X-App-Version': APP_BUILD,
      ...authHeaders,
      ...(options.headers as Record<string, string>),
    };
    // A FormData body must set its own Content-Type: the browser appends the
    // multipart boundary, and forcing application/json makes the server unable to
    // parse the parts at all.
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    let response: Response;
    let retryCount = 0;

    while (retryCount <= this.config.maxRetries) {
      try {
        response = await fetch(url, {
          ...options,
          headers,
          // Send/receive the httpOnly auth cookies (cookie transport migration).
          // Same-origin is default, but dev runs front and API on different
          // ports (cross-origin) where cookies require this. Bearer still sent.
          credentials: 'include',
        });

        // If successful, return the response
        if (response.ok) {
          // Handle empty responses (204 No Content, 205 Reset Content)
          // These status codes must not have a response body per HTTP spec
          if (response.status === 204 || response.status === 205) {
            return {} as T;
          }
          
          // For other successful responses, check content type
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return await response.json();
          }
          return {} as T;
        }

        // Handle 401 Unauthorized - try to refresh token
        if (response.status === 401 && retryCount === 0) {
          const refreshSuccess = await authService.refreshToken();
          
          if (refreshSuccess) {
            // Update headers with new token and retry
            const newAuthHeaders = authService.getAuthHeaders();
            options.headers = {
              ...(options.headers || {}),
              ...newAuthHeaders,
            };
            
            retryCount++;
            continue; // Retry the request with new token
          } else {
            // Token refresh failed, throw authentication error
            throw await this.createApiError(response, 'Authentication failed');
          }
        }

        // For other errors, throw immediately
        throw await this.createApiError(response);
      } catch (error) {
        // If it's not a 401 or we've already retried, throw the error
        if (retryCount > 0 || !(error instanceof Error && error.message.includes('401'))) {
          throw error;
        }
        
        retryCount++;
        
        // Wait before retrying
        if (retryCount <= this.config.maxRetries) {
          await new Promise(resolve => setTimeout(resolve, this.config.retryDelay));
        }
      }
    }

    // If we get here, all retries failed
    throw new Error('Request failed after maximum retries');
  }

  /**
   * Create an API error object from response
   * @param response HTTP response
   * @param defaultMessage Default error message
   * @returns ApiError object
   */
  private async createApiError(
    response: Response, 
    defaultMessage?: string
  ): Promise<ApiError> {
    let message = defaultMessage || `HTTP ${response.status}: ${response.statusText}`;
    let details: any = undefined;

    try {
      const errorData = await response.json();
      // FastAPI uses `detail`; our structured errors put payload inside it.
      // When `detail` is an object, surface its `message` (if present) as the
      // human-readable message and expose the whole body under `details`.
      const fastApiDetail = errorData?.detail;
      if (fastApiDetail && typeof fastApiDetail === 'object') {
        message = fastApiDetail.message || message;
        details = fastApiDetail;
      } else {
        message = errorData.message || fastApiDetail || errorData.error || message;
        details = errorData.details || errorData;
      }
    } catch (error) {
      // If we can't parse error response, use status text
      logger.warn('Could not parse error response', error);
    }

    return {
      message,
      status: response.status,
      code: response.status.toString(),
      details,
    };
  }

  /**
   * Login user with credentials
   * @param credentials Login credentials
   * @returns Login response
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await authService.login(credentials);
    
    if (!response.success) {
      throw new Error(response.error || 'Login failed');
    }

    // In cookie mode there are no JS tokens (they live in httpOnly cookies), so
    // only the user is required here.
    const user = authService.getCurrentUser();
    if (!user) {
      throw new Error('Login successful but failed to load user');
    }

    return {
      access_token: authService.getAccessToken() || '',
      refresh_token: authService.getRefreshToken() || '',
      token_type: 'Bearer',
      expires_in: 3600, // Default to 1 hour if not specified
      user,
    };
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    await authService.logout();
  }

  /**
   * Get current user information
   * @returns Current user
   */
  async getCurrentUser(): Promise<User> {
    const user = authService.getCurrentUser();
    
    if (!user) {
      throw new Error('User not authenticated');
    }

    return user;
  }

  /**
   * Make a GET request
   * @param endpoint API endpoint
   * @param options Request options
   * @returns Response data
   */
  async get<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  /**
   * Make a POST request
   * @param endpoint API endpoint
   * @param data Request body data
   * @param options Request options
   * @returns Response data
   */
  async post<T = any>(
    endpoint: string,
    data?: any,
    options: RequestInit = {}
  ): Promise<T> {
    // FormData goes through untouched (multipart); anything else is JSON.
    const body = data instanceof FormData
      ? data
      : (data ? JSON.stringify(data) : undefined);
    return this.request<T>(endpoint, {
      method: 'POST',
      body,
      ...options,
    });
  }

  /**
   * Make a PUT request
   * @param endpoint API endpoint
   * @param data Request body data
   * @param options Request options
   * @returns Response data
   */
  async put<T = any>(
    endpoint: string,
    data?: any,
    options: RequestInit = {}
  ): Promise<T> {
    // 🔍 DIAGNOSTIC LOG: Log what's being sent to backend
    console.log('🔍 [DIAGNOSIS] apiClient.put - Request details:');
    console.log('  Endpoint:', endpoint);
    console.log('  Raw data object:', data);
    console.log('  crop_species_id:', data?.crop_species_id);
    console.log('  crop_variety_id:', data?.crop_variety_id);
    console.log('  crop_species:', data?.crop_species);
    console.log('  crop_variety:', data?.crop_variety);
    const stringified = data ? JSON.stringify(data) : undefined;
    console.log('  JSON.stringify result:', stringified);
    console.log('=' .repeat(80));
    
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: stringified,
      ...options,
    });
  }

  /**
   * Make a PATCH request
   * @param endpoint API endpoint
   * @param data Request body data
   * @param options Request options
   * @returns Response data
   */
  async patch<T = any>(
    endpoint: string, 
    data?: any, 
    options: RequestInit = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      ...options,
    });
  }

  /**
   * Make a DELETE request
   * @param endpoint API endpoint
   * @param options Request options
   * @returns Response data
   */
  async delete<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  /**
   * GET an endpoint returning binary content (used for PDF report
   * downloads). Adds the auth header and returns the raw Blob so the
   * caller can save it to disk.
   */
  async getBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    const authHeaders = authService.getAuthHeaders();
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'GET',
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      ...options,
      credentials: 'include',
    });
    if (!response.ok) {
      throw await this.createApiError(response);
    }
    return response.blob();
  }

  /**
   * Upload a file
   * @param endpoint API endpoint
   * @param file File to upload
   * @param options Request options
   * @returns Response data
   */
  async upload<T = any>(
    endpoint: string,
    file: File,
    options: RequestInit = {}
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    const authHeaders = authService.getAuthHeaders();
    
    const response = await fetch(`${this.baseURL}${endpoint}`, {
      method: 'POST',
      headers: {
        ...authHeaders,
        ...options.headers,
      },
      body: formData,
      ...options,
      credentials: 'include',
    });

    if (!response.ok) {
      throw await this.createApiError(response);
    }

    return response.json();
  }

  /**
   * Create a new device
   * @param deviceData Device creation data
   * @returns Device response
   */
  async createDevice(deviceData: DeviceCreateData): Promise<DeviceResponse> {
    return this.post('/devices', deviceData);
  }

  /**
   * Update device settings
   * @param devEui Device DevEUI
   * @param settings Device settings to update
   * @returns Device settings response
   */
  async updateDeviceSettings(devEui: string, settings: {
    alias?: string;
    location?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<any> {
    return this.put(`/devices/${devEui}/settings`, settings);
  }

  /**
   * Transfer a device to a different farm. Requires admin+ on both farms.
   * Fails with 409 when any valve on the device is still linked to an irrigation
   * sector — the structured `error.details.linked_sectors` lists what to unlink.
   */
  async transferDevice(
    devEui: string,
    body: {
      new_farm_id: string;
      latitude?: number;
      longitude?: number;
      location_description?: string;
      notes?: string;
    },
  ): Promise<any> {
    return this.post(`/devices/${devEui}/transfer`, body);
  }

  /**
   * Release a device from its current farm (orphaning it). Requires admin+ on
   * the source farm.
   */
  async releaseDevice(devEui: string, body?: { notes?: string }): Promise<any> {
    return this.post(`/devices/${devEui}/release`, body || {});
  }

  /**
   * Fetch devices that were previously assigned to this farm but aren't now.
   */
  async getFormerDevices(farmId: string): Promise<any> {
    return this.get(`/farms/${farmId}/former-devices`);
  }

  /**
   * Full assignment history for a device (all farms, most recent first).
   */
  async getDeviceAssignmentHistory(devEui: string): Promise<any> {
    return this.get(`/devices/${devEui}/assignment-history`);
  }

  /**
   * Check if the client is authenticated
   * @returns True if authenticated
   */
  isAuthenticated(): boolean {
    return authService.isAuthenticated();
  }

  /**
   * Get authentication headers
   * @returns Authentication headers
   */
  getAuthHeaders(): Record<string, string> {
    return authService.getAuthHeaders();
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();

// Export class for testing purposes
export { ApiClient };

export default apiClient;